import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthorizationService } from '../services/authorization/authorization.service';
import { AuthorizationStateService } from '../services/authorization/authorization-state.service';
import { UserSessionService } from '../services/user-session.service';
import { Permission } from '../enums/permissions.enums';
import { AuthorizationModel } from '../models/data/authorization.model';
import { UserModel } from '../models/data/user.model';
import { SystemPageRoutes } from '../constants/constants';

export const permissionsGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthorizationService);
  const authStateService = inject(AuthorizationStateService);
  const userSessionService = inject(UserSessionService);
  const router = inject(Router);

  const requiredPermissions = route.data['permissions'] as Permission | Permission[];
  const requireAll = route.data['requireAll'] ?? true;
  // Platform-level routes (feature flag administration) set this so a member role that happened to
  // carry the same permission could not satisfy the check, matching ifHasPermission's option.
  const userPermissionOnly = route.data['userPermissionOnly'] ?? false;

  if (!requiredPermissions) {
    return true;
  }

  const authorizationState = authStateService.authorizationState();

  if (!authorizationState) {
    return router.createUrlTree(SystemPageRoutes.FORBIDDEN());
  }

  if (PermissionUtilities.shouldBypassPermissionCheck(router, route, state, userSessionService.activeUser(), authorizationState) === true) {
    return true;
  }

  // A route that names an organization (today only the organization profile) is authorized against
  // THAT organization, not whichever one happens to be active. Such a route is reachable for any
  // organization the viewer belongs to and deliberately does not change the active organization, so
  // an Admin of the active organization would otherwise satisfy the check for an organization they
  // hold no role in — the membership bypass above only covers viewers who are members of it
  // (DEV-2643).
  const routeOrganizationId = Number(route.paramMap.get('organizationId')) || undefined;
  const organizationId = routeOrganizationId ?? userSessionService.activeOrganization()?.organizationId;

  const permissionsToCheck = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];

  let hasAccess: boolean;

  if (requireAll) {
    hasAccess = permissionsToCheck.every(permission =>
      authService.hasPermission(authorizationState, permission, organizationId, userPermissionOnly)
    );
  } else {
    hasAccess = permissionsToCheck.some(permission =>
      authService.hasPermission(authorizationState, permission, organizationId, userPermissionOnly)
    );
  }

  if (hasAccess) {
    return true;
  } else {
    return router.createUrlTree(SystemPageRoutes.FORBIDDEN());
  }
};

export class PermissionUtilities {

  public static shouldBypassPermissionCheck(_router: Router, route: ActivatedRouteSnapshot, state: RouterStateSnapshot, _activeUser: UserModel | undefined, authorizationState: AuthorizationModel | undefined): boolean {

    //Member Profile
    if (state.url.includes("/members/profile/")) {
      const memberId = route.paramMap.get("memberId");

      if (!memberId) {
        return false;
      }

      const memberIds = authorizationState?.memberPermissions?.map(mp => mp.memberId);

      if (memberIds?.includes(+memberId)) {
        return true;
      }
      else {
        return false;
      }

    //Organization Profile
    } else if (state.url.includes("/organizations/profile/")) {
      const organizationId = route.paramMap.get("organizationId");
      
      if (!organizationId) {
        return false;
      }

      const organizationIds = authorizationState?.memberPermissions?.map(mp => mp.organizationId);

      if (organizationIds?.includes(+organizationId)) {
        return true;
      }
      else {
        return false;
      }
    } else {
      return false;
    }
  }
}

